import React, { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Crown,
  Users,
  Heart,
  TrendingUp,
  Star,
  Calendar,
  Search,
  ChevronDown,
} from "lucide-react";
import { CreatorTimeline } from '@/components/CreatorSpace/CreatorTimeline';
import { CreatorList } from '@/components/CreatorSpace/CreatorList';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { creatorSpaceApi } from '@/lib/api/creatorSpace';
import { useTranslation, Trans } from 'react-i18next';
import { PageLayout } from '@/layouts';
import { VStack, HStack, Grid } from '@/components/core';

const CreatorSpace: React.FC = () => {
  const { t } = useTranslation('creatorSpace');
  const [activeTab, setActiveTab] = useState<'timeline' | 'creators'>('creators');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('subscribers');
  const [filterTier, setFilterTier] = useState('all');
  const { user } = useAuth();
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  

  // Hat der Nutzer ein aktives Creator-Abo? Dann auf Timeline umschalten
  useEffect(() => {
    let isMounted = true;
    const checkSubscriptions = async () => {
      try {
        if (!user) return; // unangemeldet: bleibt bei "creators"
        const subs = await creatorSpaceApi.getMySubscriptions();
        const now = new Date();
        const hasActive = subs.some(s => s.status === 'active' && new Date(s.expires_at) > now);
        if (isMounted && hasActive) {
          setActiveTab('timeline');
        }
        if (isMounted) {
          setHasActiveSubscription(hasActive);
        }
      } catch (_e) {
        // still fail-safe: bei Fehler bleibt creators als Default
      }
    };
    void checkSubscriptions();
    return () => { isMounted = false; };
  }, [user]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'timeline':
        return <CreatorTimeline />;
      case 'creators':
        return <CreatorList />;
      default:
        return <CreatorTimeline />;
    }
  };

  return (
    <PageLayout maxWidth="xl" padding="md">
      <VStack gap="md">
        {/* Compact Hero Banner - nur anzeigen, wenn kein aktives Creator-Abo vorhanden ist */}
        {!(user && hasActiveSubscription) && (
          <div className="design-card p-token-md md:p-token-lg fade-in-up">
            <VStack gap="md">
              {/* Main Banner Content */}
              <HStack gap="md" align="start" className="flex-col sm:flex-row">
                {/* Icon */}
                <div className="w-12 h-12 md:w-14 md:h-14 gradient-primary-smooth rounded-token-card flex items-center justify-center flex-shrink-0">
                  <Crown className="h-6 w-6 md:h-7 md:w-7 text-white" />
                </div>

                {/* Content */}
                <VStack gap="xs" className="flex-1 min-w-0">
                  <HStack gap="sm" align="center" wrap>
                    <h1 className="text-xl md:text-2xl font-bold design-text-primary">
                      {t('hero.title')} <span className="gradient-text">{t('hero.subtitle')}</span>
                    </h1>
                  </HStack>
                  <p className="text-sm md:text-base design-text-secondary leading-relaxed">
                    <Trans i18nKey="hero.description" ns="creatorSpace" />
                  </p>
                </VStack>

                {/* CTA Buttons - Desktop: inline, Mobile: full width below */}
                <HStack gap="sm" className="hidden sm:flex flex-shrink-0">
                  {user ? (
                    <Button
                      variant="gradient"
                      size="sm"
                      onClick={() => setActiveTab('creators')}
                    >
                      <Crown className="h-4 w-4 mr-token-xs" />
                      {t('hero.cta.discover')}
                    </Button>
                  ) : (
                    <>
                      <Link to="/auth?tab=register">
                        <Button variant="gradient" size="sm">
                          {t('hero.cta.join')}
                        </Button>
                      </Link>
                      <Link to="/auth?tab=login">
                        <Button variant="glass" size="sm">
                          {t('hero.cta.login')}
                        </Button>
                      </Link>
                    </>
                  )}
                </HStack>
              </HStack>

              {/* Mobile CTA Buttons */}
              <HStack gap="sm" className="sm:hidden w-full">
                {user ? (
                  <Button
                    variant="gradient"
                    className="flex-1"
                    onClick={() => setActiveTab('creators')}
                  >
                    <Crown className="h-4 w-4 mr-token-xs" />
                    {t('hero.cta.discover')}
                  </Button>
                ) : (
                  <>
                    <Link to="/auth?tab=register" className="flex-1">
                      <Button variant="gradient" className="w-full">
                        {t('hero.cta.join')}
                      </Button>
                    </Link>
                    <Link to="/auth?tab=login" className="flex-1">
                      <Button variant="glass" className="w-full">
                        {t('hero.cta.login')}
                      </Button>
                    </Link>
                  </>
                )}
              </HStack>

              {/* Feature Chips - horizontal scrollable */}
              <HStack gap="sm" className="overflow-x-auto pb-token-xs -mx-token-xs px-token-xs scrollbar-hide">
                <Badge variant="outline" className="flex-shrink-0 gap-token-xs.5 px-token-sm py-token-xs bg-white/5 border-white/10">
                  <Star className="h-3.5 w-3.5 text-warning" />
                  <span className="text-xs design-text-secondary">{t('features.exclusive.title')}</span>
                </Badge>
                <Badge variant="outline" className="flex-shrink-0 gap-token-xs.5 px-token-sm py-token-xs bg-white/5 border-white/10">
                  <Heart className="h-3.5 w-3.5 text-error" />
                  <span className="text-xs design-text-secondary">{t('features.interaction.title')}</span>
                </Badge>
                <Badge variant="outline" className="flex-shrink-0 gap-token-xs.5 px-token-sm py-token-xs bg-white/5 border-white/10">
                  <TrendingUp className="h-3.5 w-3.5 text-success" />
                  <span className="text-xs design-text-secondary">{t('features.timeline.title')}</span>
                </Badge>
                <Badge variant="outline" className="flex-shrink-0 gap-token-xs.5 px-token-sm py-token-xs bg-white/5 border-white/10">
                  <Users className="h-3.5 w-3.5 text-info" />
                  <span className="text-xs design-text-secondary">{t('features.flexible.title')}</span>
                </Badge>
              </HStack>

              {/* Expandable Feature Details */}
              <button
                onClick={() => setShowFeatures(!showFeatures)}
                className="flex items-center gap-token-xs.5 text-xs design-text-secondary hover:text-white transition-colors self-start"
              >
                <span>{showFeatures ? t('hero.hideFeatures', { defaultValue: 'Weniger anzeigen' }) : t('hero.showFeatures', { defaultValue: 'Mehr erfahren' })}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFeatures ? 'rotate-180' : ''}`} />
              </button>

              {/* Expanded Feature Details */}
              {showFeatures && (
                <Grid cols={1} colsMd={2} gap="sm" className="pt-token-sm border-t border-white/10 animate-in fade-in slide-in-from-top-2 duration-200">
                  <HStack gap="sm" className="p-token-sm rounded-token-button bg-white/5">
                    <Star className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium design-text-primary">{t('features.exclusive.title')}</p>
                      <p className="text-xs design-text-secondary mt-0.5">{t('features.exclusive.description')}</p>
                    </div>
                  </HStack>
                  <HStack gap="sm" className="p-token-sm rounded-token-button bg-white/5">
                    <Heart className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium design-text-primary">{t('features.interaction.title')}</p>
                      <p className="text-xs design-text-secondary mt-0.5">{t('features.interaction.description')}</p>
                    </div>
                  </HStack>
                  <HStack gap="sm" className="p-token-sm rounded-token-button bg-white/5">
                    <TrendingUp className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium design-text-primary">{t('features.timeline.title')}</p>
                      <p className="text-xs design-text-secondary mt-0.5">{t('features.timeline.description')}</p>
                    </div>
                  </HStack>
                  <HStack gap="sm" className="p-token-sm rounded-token-button bg-white/5">
                    <Users className="h-5 w-5 text-info flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium design-text-primary">{t('features.flexible.title')}</p>
                      <p className="text-xs design-text-secondary mt-0.5">{t('features.flexible.description')}</p>
                    </div>
                  </HStack>
                </Grid>
              )}
            </VStack>
          </div>
        )}

        {/* Navigation Tabs - Compact */}
        <VStack gap="md" align="center" className="w-full">
          {/* For users with subscription, show a minimal header */}
          {user && hasActiveSubscription && (
            <HStack gap="md" align="center" className="fade-in-up">
              <div className="w-10 h-10 gradient-primary-smooth rounded-token-card flex items-center justify-center">
                <Crown className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold design-text-primary">
                Creator Space
              </h1>
            </HStack>
          )}

          {/* Tabs and Filters - stacked on mobile, row on desktop */}
          <VStack gap="md" align="center" className="fade-in-up w-full">
            {/* Tabs - full width on mobile, centered on desktop */}
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'timeline' | 'creators')} className="w-full md:w-auto">
              <TabsList className="grid grid-cols-2 w-full md:w-auto md:inline-flex">
                <TabsTrigger value="timeline" className="flex items-center gap-token-sm">
                  <Calendar className="h-4 w-4" />
                  {t('tabs.timeline')}
                </TabsTrigger>
                <TabsTrigger value="creators" className="flex items-center gap-token-sm">
                  <Users className="h-4 w-4" />
                  {t('tabs.discover')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Filter Section */}
            {activeTab === 'creators' && (
              <>
                {/* Desktop Layout */}
                <HStack gap="sm" align="center" justify="center" className="hidden md:flex w-full">
                  <div className="relative w-56">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted z-10 pointer-events-none" />
                    <Input
                      placeholder={t('filter.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full h-9"
                    />
                  </div>
                  <HStack gap="sm">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-32 h-9 design-glass design-text-primary text-sm">
                        <SelectValue placeholder={t('filter.sort.label')} />
                      </SelectTrigger>
                      <SelectContent className="design-card">
                        <SelectItem value="subscribers">{t('filter.sort.subscribers')}</SelectItem>
                        <SelectItem value="posts">{t('filter.sort.posts')}</SelectItem>
                        <SelectItem value="earnings">{t('filter.sort.earnings')}</SelectItem>
                        <SelectItem value="price">{t('filter.sort.price')}</SelectItem>
                        <SelectItem value="name">{t('filter.sort.name')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterTier} onValueChange={setFilterTier}>
                      <SelectTrigger className="w-32 h-9 design-glass design-text-primary text-sm">
                        <SelectValue placeholder={t('filter.tier.label')} />
                      </SelectTrigger>
                      <SelectContent className="design-card">
                        <SelectItem value="all">{t('filter.tier.all')}</SelectItem>
                        <SelectItem value="basic">{t('filter.tier.basic')}</SelectItem>
                        <SelectItem value="premium">{t('filter.tier.premium')}</SelectItem>
                        <SelectItem value="exclusive">{t('filter.tier.exclusive')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </HStack>
                </HStack>

                {/* Mobile Layout */}
                <VStack gap="sm" className="md:hidden w-full">
                  <div className="relative w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted z-10 pointer-events-none" />
                    <Input
                      placeholder={t('filter.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full h-9"
                    />
                  </div>
                  <Grid cols={2} gap="sm" className="w-full">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-full h-9 design-glass design-text-primary text-xs sm:text-sm">
                        <SelectValue placeholder={t('filter.sort.label')} />
                      </SelectTrigger>
                      <SelectContent className="design-card">
                        <SelectItem value="subscribers">{t('filter.sort.subscribers')}</SelectItem>
                        <SelectItem value="posts">{t('filter.sort.posts')}</SelectItem>
                        <SelectItem value="earnings">{t('filter.sort.earnings')}</SelectItem>
                        <SelectItem value="price">{t('filter.sort.price')}</SelectItem>
                        <SelectItem value="name">{t('filter.sort.name')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterTier} onValueChange={setFilterTier}>
                      <SelectTrigger className="w-full h-9 design-glass design-text-primary text-xs sm:text-sm">
                        <SelectValue placeholder={t('filter.tier.label')} />
                      </SelectTrigger>
                      <SelectContent className="design-card">
                        <SelectItem value="all">{t('filter.tier.all')}</SelectItem>
                        <SelectItem value="basic">{t('filter.tier.basic')}</SelectItem>
                        <SelectItem value="premium">{t('filter.tier.premium')}</SelectItem>
                        <SelectItem value="exclusive">{t('filter.tier.exclusive')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Grid>
                </VStack>
              </>
            )}
          </VStack>
        </VStack>

        {/* Content Section */}
        <div className="fade-in-up">
          {renderTabContent()}
        </div>
      </VStack>
    </PageLayout>
  );
};

export default CreatorSpace;
